import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Input,
  makeStyles,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@material-ui/core';
import { styled } from '@material-ui/core/styles';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import {
  ADDITIONAL_BILLING_STATUS,
  ADDITIONAL_BILLING_VISIBLE_STATUS,
  getAdditionalBillingDisplayStatus,
  getBillingIssueDisplayLabel,
} from 'doc-mate-store/lib/constants/load';
import { useStores } from 'doc-mate-store/lib/hooks';
import React, { useCallback, useState } from 'react';
import BillingIssueModal from './BillingIssueModal';
import { BillingStatusConfirmDialog } from './BillingStatusConfirmDialog';
import InvalidStatusTransitionModal from './InvalidStatusTransitionModal';

type SelectProps = {
  status: ADDITIONAL_BILLING_STATUS;
  loadId: number;
  isBilling: boolean;
  billingIssueDescription?: string;
};

const SelectChip = styled('div')({
  display: 'inline-block',
  padding: '0px 5px',
  height: '20px',
  borderRadius: '10px',
  lineHeight: '20px',
  fontSize: '12px',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  overflow: 'hidden',

  '&.denied, &.billing_issue': {
    backgroundColor: '#F38686',
    color: 'white',
  },
  '&.pending': {
    backgroundColor: '#FADFA3',
  },
  '&.approved': {
    backgroundColor: '#A6E7C1',
  },
  '&.invoiced': {
    backgroundColor: '#CAA2EE',
  },
});

const ADDITIONAL_BILLING_ISSUE_OPTIONS = ['Missing Pages', 'Blurry', 'Other'];

export const AdditionalBillingSelectWrapper: React.FC<SelectProps> = props => {
  const [open, setOpen] = useState(false);
  const [billingIssueModalOpen, setBillingIssueModalOpen] = useState(false);
  const [
    pendingStatus,
    setPendingStatus,
  ] = useState<ADDITIONAL_BILLING_STATUS | null>(null);

  // Invalid Transition Modal State
  const [openInvalidModal, setOpenInvalidModal] = useState(false);
  const [invalidMessage, setInvalidMessage] = useState('');

  // Confirmation & Denial Dialog States
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogMessage, setConfirmDialogMessage] = useState<
    React.ReactNode
  >('');
  const [deniedDialogOpen, setDeniedDialogOpen] = useState(false);
  const [deniedReason, setDeniedReason] = useState('');

  const classes = useStyles();
  const { rootStore } = useStores();
  const { status, loadId, isBilling, billingIssueDescription } = props;
  const isInvoiced = status === ADDITIONAL_BILLING_STATUS.INVOICED;

  const executeStatusUpdate = useCallback(
    async (
      newStatus: ADDITIONAL_BILLING_STATUS,
      data?: { deniedReason?: string; descriptions?: string[] },
    ) => {
      const load = rootStore.loadTableRows.get(`${loadId}`);
      if (load) {
        const response = await load.updateAdditionalBillingStatus(
          newStatus,
          data,
        );
        if (response && !response.ok) {
          const errorMsg =
            typeof response.errors === 'string'
              ? response.errors
              : response.errors?.status?.[0] || 'Failed to update status.';

          setInvalidMessage(errorMsg);
          setOpenInvalidModal(true);
        }
      }
    },
    [loadId, rootStore.loadTableRows],
  );

  const handleChange = useCallback(
    async (event: React.ChangeEvent<{ value: unknown }>) => {
      event.stopPropagation();
      const newValue = event.target.value as ADDITIONAL_BILLING_STATUS;
      if (newValue === status) return;

      setOpen(false);

      // 1. Flagging a Billing Issue opens multi-select modal
      if (newValue === ADDITIONAL_BILLING_STATUS.BILLING_ISSUE) {
        setPendingStatus(newValue);
        setBillingIssueModalOpen(true);
        return;
      }

      // 2. Denial requires reason entry
      if (newValue === ADDITIONAL_BILLING_STATUS.DENIED) {
        setPendingStatus(newValue);
        setDeniedReason('');
        setDeniedDialogOpen(true);
        return;
      }

      // 3. Client Validation: Only Approved items can move to Invoiced
      const isPendingOrDenied =
        status === ADDITIONAL_BILLING_STATUS.PENDING ||
        status === ADDITIONAL_BILLING_STATUS.DENIED;

      if (
        isPendingOrDenied &&
        newValue === ADDITIONAL_BILLING_STATUS.INVOICED
      ) {
        setInvalidMessage(
          'Only items with "Approved" status can be moved to "Invoiced".',
        );
        setOpenInvalidModal(true);
        return;
      }

      // 4. Default Confirmation Dialog for status changes
      setPendingStatus(newValue);
      setConfirmDialogMessage(
        `Are you sure you want to change status from "${getAdditionalBillingDisplayStatus(
          status,
        )}" to "${getAdditionalBillingDisplayStatus(newValue)}"?`,
      );
      setConfirmDialogOpen(true);
    },
    [status],
  );

  const handleConfirmBillingIssue = useCallback(
    async (descriptions: string[]) => {
      if (pendingStatus === ADDITIONAL_BILLING_STATUS.BILLING_ISSUE) {
        await executeStatusUpdate(pendingStatus, { descriptions });
      }
      setBillingIssueModalOpen(false);
      setPendingStatus(null);
    },
    [pendingStatus, executeStatusUpdate],
  );

  const handleConfirmDialog = useCallback(async () => {
    if (pendingStatus) {
      await executeStatusUpdate(pendingStatus);
    }
    setConfirmDialogOpen(false);
    setPendingStatus(null);
  }, [pendingStatus, executeStatusUpdate]);

  const handleCancelDialog = useCallback(() => {
    setConfirmDialogOpen(false);
    setPendingStatus(null);
  }, []);

  const handleConfirmDenied = useCallback(async () => {
    if (pendingStatus === ADDITIONAL_BILLING_STATUS.DENIED) {
      await executeStatusUpdate(pendingStatus, { deniedReason });
    }
    setDeniedDialogOpen(false);
    setPendingStatus(null);
    setDeniedReason('');
  }, [pendingStatus, deniedReason, executeStatusUpdate]);

  const handleCancelDenied = useCallback(() => {
    setDeniedDialogOpen(false);
    setPendingStatus(null);
    setDeniedReason('');
  }, []);

  const handleOpen = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen(true);
  }, []);

  const handleMouseDown = useCallback(() => {
    if (open) setOpen(false);
  }, [open]);

  const iconComponent = useCallback(
    () => (
      <span onClick={handleOpen}>
        {open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
      </span>
    ),
    [handleOpen, open],
  );

  return (
    <>
      <Select
        className={classes.select}
        fullWidth
        input={<Input disableUnderline />}
        onChange={handleChange}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        value={status}
        open={open}
        disabled={!isBilling && isInvoiced}
        IconComponent={iconComponent}
        onMouseDown={handleMouseDown}
        renderValue={selected => {
          const currentStatus = selected as ADDITIONAL_BILLING_STATUS;
          let chipLabel = getAdditionalBillingDisplayStatus(currentStatus);

          if (currentStatus === ADDITIONAL_BILLING_STATUS.BILLING_ISSUE) {
            const isMultipleOrOther =
              billingIssueDescription && billingIssueDescription.includes(',');

            chipLabel = isMultipleOrOther
              ? 'Billing Issues - Other'
              : getBillingIssueDisplayLabel(billingIssueDescription);
          }

          const chipContent = (
            <SelectChip className={currentStatus}>{chipLabel}</SelectChip>
          );

          if (currentStatus === ADDITIONAL_BILLING_STATUS.BILLING_ISSUE) {
            return (
              <Tooltip
                title={billingIssueDescription ? billingIssueDescription : ''}
                arrow
                placement="top"
              >
                {chipContent}
              </Tooltip>
            );
          }

          return chipContent;
        }}
      >
        {ADDITIONAL_BILLING_VISIBLE_STATUS.map((stat: any) => (
          <MenuItem
            key={stat}
            disabled={
              !isBilling &&
              (stat === ADDITIONAL_BILLING_STATUS.INVOICED ||
                stat === ADDITIONAL_BILLING_STATUS.BILLING_ISSUE ||
                status === ADDITIONAL_BILLING_STATUS.INVOICED)
            }
            value={stat}
          >
            <SelectChip className={stat}>
              {getAdditionalBillingDisplayStatus(stat)}
            </SelectChip>
          </MenuItem>
        ))}
      </Select>

      {billingIssueModalOpen && (
        <BillingIssueModal
          loading={false}
          options={ADDITIONAL_BILLING_ISSUE_OPTIONS}
          onConfirm={handleConfirmBillingIssue}
          onCancel={() => {
            setBillingIssueModalOpen(false);
            setPendingStatus(null);
          }}
        />
      )}

      <BillingStatusConfirmDialog
        open={confirmDialogOpen}
        title="Confirm Status Change"
        message={confirmDialogMessage}
        showConfirmBtn
        onConfirm={handleConfirmDialog}
        onCancel={handleCancelDialog}
      />

      <Dialog
        open={deniedDialogOpen}
        onClose={handleCancelDenied}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle className={classes.dialogTitle}>
          Reason for Denial
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Reason / Person who denied"
            fullWidth
            variant="outlined"
            value={deniedReason}
            onChange={e => setDeniedReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button
            onClick={handleCancelDenied}
            className={classes.cancelBtn}
            disableElevation
          >
            CANCEL
          </Button>
          <Button
            onClick={handleConfirmDenied}
            className={classes.confirmBtn}
            disableElevation
            disabled={!deniedReason.trim()}
          >
            CONFIRM
          </Button>
        </DialogActions>
      </Dialog>

      <InvalidStatusTransitionModal
        open={openInvalidModal}
        message={invalidMessage}
        onClose={() => {
          setOpenInvalidModal(false);
          setInvalidMessage('');
        }}
      />
    </>
  );
};

const useStyles = makeStyles(theme => ({
  select: {
    '& > .MuiSelect-select.MuiSelect-select': {
      backgroundColor: 'transparent',
      paddingRight: 0,
    },
  },
  popUpText: {
    fontSize: 12,
    padding: 5,
  },
  dialogTitle: {
    textAlign: 'center',
    '& h2': {
      fontWeight: 700,
      textTransform: 'uppercase',
      fontSize: '1.1rem',
      color: '#424242',
    },
  },
  dialogActions: {
    justifyContent: 'center',
    paddingBottom: theme.spacing(3),
    gap: theme.spacing(2),
  },
  cancelBtn: {
    backgroundColor: '#ef5350',
    color: '#ffffff',
    fontWeight: 700,
    padding: '8px 28px',
    borderRadius: 6,
    '&:hover': {
      backgroundColor: '#e53935',
    },
  },
  confirmBtn: {
    backgroundColor: '#4884a5',
    color: '#ffffff',
    fontWeight: 700,
    padding: '8px 28px',
    borderRadius: 6,
    '&:hover': {
      backgroundColor: '#396d8a',
    },
    '&.Mui-disabled': {
      backgroundColor: '#a3c2d4',
      color: '#ffffff',
    },
  },
}));

export default React.memo(AdditionalBillingSelectWrapper);
